/**
 * Feature E05-F06 — "Email-композиция и отправка (Stage 4/5)" request/response
 * contracts. 1:1 port of the legacy n8n branches that compose and send the
 * stage-4/5/6 emails:
 *
 *   - Draft.md           → save_pre_kickoff_draft   (UPSERT of pre_kickoff_draft_<A|B|C>)
 *   - Email.md           → send_pre_kickoff_email   (atomic send + stage/event log)
 *   - Email.md (S5Email) → stage-5 send             (client_followup / sourcing_sheet / internal_notification)
 *   - Email.md (S6)      → final_package send
 *   - Notif.md           → get_notification_defaults (auto-recipients + templates)
 *
 * Single source of truth for:
 *   - POST /projects/:id/prekickoff/draft   (T05 — UPSERT draft)
 *   - POST /projects/:id/prekickoff/send    (T01/T02/T05 — atomic send)
 *   - POST /projects/:id/email/send         (T01/T04 — stage-5/6 universal send)
 *   - GET  /projects/:id/email/notification-defaults (T04 — auto-recipients)
 *
 * IDOR invariant (carried over unchanged): the actor (`userId`/`created_by`/the
 * sender mailbox) is NEVER part of any *input* schema here — the api derives the
 * actor from the validated session, so a request can never forge it (Email.md
 * gotcha: legacy `$('DB: Validate Session1').first().json.id`). Likewise the
 * sender `from_email` is the mailbox the API selects, never a body field.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
import { renderFreeMarkdown } from '../markdown.js';
import { looksLikeHtml, normalizeRichText, isRichTextEmpty } from '../richText.js';
// ---------------------------------------------------------------------------
// Draft option (pre_kickoff_draft_<A|B|C>) — Draft.md / Email.md
// ---------------------------------------------------------------------------
/**
 * Draft variant label. Normalised to upper-case; defaults to `A` (legacy
 * `(body.option || 'A').toUpperCase()` in both Draft and Email branches).
 */
export const EMAIL_DRAFT_OPTIONS = ['A', 'B', 'C'];
/** Coerce any input to a valid option (A/B/C), defaulting to 'A' (legacy parity). */
export const emailDraftOptionSchema = z
    .union([z.string(), z.undefined(), z.null()])
    .transform((v) => {
    const up = String(v ?? 'A')
        .trim()
        .toUpperCase();
    return EMAIL_DRAFT_OPTIONS.includes(up) ? up : 'A';
})
    .pipe(z.enum(EMAIL_DRAFT_OPTIONS));
/** Artifact `type` per draft option (legacy `pre_kickoff_draft_<A|B|C>`). */
export function preKickoffDraftType(option) {
    return `pre_kickoff_draft_${option}`;
}
// ---------------------------------------------------------------------------
// Draft content shape (project_artifacts.content for pre_kickoff_draft_*)
// ---------------------------------------------------------------------------
/**
 * The fields the Draft branch serialises into the artifact content. Any extra
 * keys are dropped.
 *
 * `to` is a SNAPSHOT, not the authority (KS2-08, 2026-07-31). It holds whatever
 * address the analysis put there when the draft was generated; the address the
 * send actually uses comes from the project's addressee card
 * (`project_contacts` → services/emailRecipients.ts). That inversion is the fix
 * for «письмо ушло не назначенному новому основному контакту, а первому»: the
 * recipient lived in two places, and changing the one the user was shown left the
 * one the sender read untouched. This field survives as the fallback for projects
 * that have no addressee row at all.
 */
export const emailDraftContentSchema = z.object({
    to: z.string().default(''),
    subject: z.string().default(''),
    body: z.string().default(''),
});
// ---------------------------------------------------------------------------
// T05 — POST /projects/:id/prekickoff/draft (save_pre_kickoff_draft, UPSERT)
// ---------------------------------------------------------------------------
/**
 * save_pre_kickoff_draft request body. `draft` defaults to `{}` (legacy
 * `body.draft || {}`); `option` defaults to 'A'. The project id comes from the
 * URL path (not the body) so it can't be forged — IDOR rule.
 */
export const saveDraftRequestSchema = z.object({
    draft: emailDraftContentSchema.partial().optional().default({}),
    option: emailDraftOptionSchema.optional().default('A'),
});
/** save_pre_kickoff_draft response — `{ success:true }` (Draft.md "Return Result"). */
export const saveDraftResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
});
// ---------------------------------------------------------------------------
// T01/T02/T05 — POST /projects/:id/prekickoff/send (send_pre_kickoff_email)
// ---------------------------------------------------------------------------
/**
 * send_pre_kickoff_email request body. `option` selects the saved draft
 * (pre_kickoff_draft_<OPTION>); `in_reply_to` (optional) is forwarded as the
 * SMTP Reply-To header for threading (Email.md — SMTP branch only). The recipient
 * /subject/body all come from the stored draft, never the body — the client only
 * picks which variant to send.
 */
export const sendPreKickoffRequestSchema = z.object({
    option: emailDraftOptionSchema.optional().default('A'),
    in_reply_to: z.string().optional(),
});
/** send_pre_kickoff_email response — `{ success:true }` (Email.md "Return Result"). */
export const sendPreKickoffResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
});
// ---------------------------------------------------------------------------
// T01/T04 — POST /projects/:id/email/send (S5Email / S6 universal send)
// ---------------------------------------------------------------------------
/**
 * Stage-5/6 item keys (S5Email.md mapping). The api maps each to its artifact
 * `type` (note `sourcing_sheet_email → sourcing_sheet`) and its event_type.
 * `final_package` is the S6 branch (no artifact mark, only emails + event).
 */
export const STAGE5_ITEM_KEYS = [
    'client_followup_email',
    'sourcing_sheet_email',
    'kickoff_sheet',
    'internal_notification',
    'final_package',
];
export const stage5ItemKeySchema = z.enum(STAGE5_ITEM_KEYS);
/**
 * The composed email payload (To/Subject/Body + optional CC / BCC).
 *
 * `to` stays a comma/semicolon-separated string (legacy wire shape — several
 * addresses are allowed and split by the transport); `cc` / `bcc` are arrays.
 * E05-F06-T11: `bcc` added — Ksenia sends the final package to several people
 * with blind copies; it is honoured by both transports (Graph `bccRecipients`,
 * nodemailer `bcc`).
 */
export const composedEmailSchema = z.object({
    to: z.string().min(1, 'Recipient is required'),
    subject: z.string().default(''),
    body: z.string().default(''),
    cc: z.array(z.string()).optional().default([]),
    bcc: z.array(z.string()).optional(),
});
/**
 * Universal stage-5/6 send request (S5Email "Build SQL" validates projectId +
 * item_key + email.to). projectId comes from the URL path.
 */
export const sendStage5RequestSchema = z.object({
    item_key: stage5ItemKeySchema,
    email: composedEmailSchema,
});
/** Universal stage-5/6 send response (`{ success:true, message }`). */
export const sendStage5ResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string(),
});
// ---------------------------------------------------------------------------
// T04 — GET /projects/:id/email/notification-defaults (get_notification_defaults)
// ---------------------------------------------------------------------------
/** One auto-recipient `{name,email}` (Notif.md json_build_object). */
export const notificationRecipientSchema = z.object({
    name: z.string(),
    email: z.string(),
});
/**
 * get_notification_defaults response (Notif.md "Return Defaults"). `recipients`
 * is the deterministic concat of staff (category='notification', active) THEN
 * the team lead; `template_*` are the two `ai_prompts` templates (or '').
 */
export const notificationDefaultsResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    recipients: z.array(notificationRecipientSchema),
    template_standard: z.string(),
    template_confidential: z.string(),
});
// ---------------------------------------------------------------------------
// Item-key → artifact type / event type maps (S5Email.md dictionaries)
// ---------------------------------------------------------------------------
/**
 * item_key → project_artifacts.type. NB: `sourcing_sheet_email` maps to the
 * `sourcing_sheet` artifact (the name differs from the key — S5Email.md gotcha).
 * `final_package` has no artifact to mark (S6 writes only emails + event).
 */
export const STAGE5_ARTIFACT_TYPE = {
    client_followup_email: 'client_followup_email',
    sourcing_sheet_email: 'sourcing_sheet',
    kickoff_sheet: 'kickoff_sheet',
    internal_notification: 'internal_notification',
    final_package: null,
};
/** item_key → project_events.event_type (S5Email.md / S6.md). */
export const STAGE5_EVENT_TYPE = {
    client_followup_email: 'client_followup_email_sent',
    sourcing_sheet_email: 'sourcing_sheet_email_sent',
    kickoff_sheet: 'kickoff_sheet_sent',
    internal_notification: 'internal_notification_sent',
    final_package: 'final_package_sent',
};
/**
 * Items that get the recruiter signature appended (project-docs.js
 * `sigItems = ['client_followup_email','final_package']`). The pre-kickoff send
 * also appends the signature (T04 acceptance), wired in the api, not here.
 */
export const SIGNATURE_ITEM_KEYS = [
    'client_followup_email',
    'final_package',
];
/** True when `itemKey` should get the recruiter `email_signature` appended. */
export function shouldAppendSignature(itemKey) {
    return SIGNATURE_ITEM_KEYS.includes(itemKey);
}
/**
 * The signature to actually use: the recruiter's `email_signature`, or their NAME
 * when that field is empty.
 *
 * B4/KS-15. `generate_followup_email` ends with a hard-coded «Kind Regards,» block
 * and receives no sender-name variable — the name is supposed to arrive with the
 * signature. With `users.email_signature` empty (which it was for every user on
 * dev, `ksenia` included) the client received a sign-off with nothing after it.
 * A dangling greeting is worse than a missing signature, so an empty field falls
 * back to the name rather than to nothing.
 *
 * Emptiness is measured with {@link isRichTextEmpty}, not `.trim()`: once the
 * signature is edited in the rich-text editor an "empty" one is `<p></p>`, which
 * trims to a non-empty string and would defeat the fallback. An image-only
 * signature (a logo, no text) is deliberately NOT empty.
 */
export function effectiveSignature(signature, name) {
    const sig = (signature ?? '').trim();
    if (sig && !isRichTextEmpty(sig))
        return sig;
    return (name ?? '').trim();
}
/**
 * Append a recruiter signature to an email body.
 *
 * Two modes, chosen by the CONTENT rather than by a flag, because body and
 * signature migrate to HTML independently (a recruiter may still have a plain-text
 * signature saved while the body already comes from the editor, and vice versa):
 *
 *   - plain text + plain text → the legacy `body + '\n\n' + sig`, byte for byte,
 *     so drafts already stored in the DB are unaffected;
 *   - anything HTML → BOTH sides are normalized to HTML and concatenated as
 *     blocks. Without this, the legacy `\n\n` is collapsed as whitespace by every
 *     mail client and the signature renders glued to the last paragraph; and a
 *     plain-text signature appended raw to an HTML body would lose its line breaks
 *     entirely (`renderFreeMarkdown` turns them into `<br>`).
 *
 * Empty/whitespace-only signature → body unchanged (T04 acceptance:
 * "email_signature пуст → без подписи без ошибки"). A visually-empty HTML
 * signature (`<p></p>` from the editor) counts as empty too — otherwise the body
 * grows a blank block.
 *
 * The caller sanitizes: this returns HTML assembled from two untrusted values and
 * is NOT a sanitizing step (`api/services/htmlSanitize.ts` does that at the gate).
 */
export function appendSignature(body, signature) {
    const sig = (signature ?? '').trim();
    if (!sig || isRichTextEmpty(sig))
        return body;
    if (looksLikeHtml(body) || looksLikeHtml(sig)) {
        return `${normalizeRichText(body, renderFreeMarkdown)}${normalizeRichText(sig, renderFreeMarkdown)}`;
    }
    return `${body}\n\n${sig}`;
}
// ---------------------------------------------------------------------------
// E03-F04-T03 — follow-up email: no AI signature, CC union
// ---------------------------------------------------------------------------
/** The fixed closing of `generate_followup_email` ("Kind Regards," — DO NOT EDIT). */
const FOLLOWUP_CLOSING_RE = /kind\s+regards\s*,?/gi;
/**
 * Longest text (tags stripped) after the closing that is still treated as an
 * AI-written sign-off. Anything longer is real content and is left alone — a
 * wrong cut would silently delete part of a client email.
 */
const MAX_SIGNATURE_TAIL_CHARS = 300;
/** A bracketed signature placeholder on its own line: `[Your Name]`, `[Signature]`… */
const SIGNATURE_PLACEHOLDER_RE = /(?:<p[^>]*>\s*|<br\s*\/?>\s*|\n\s*)?\[[^\]\n<>]{0,40}(?:name|signature|title|position|company|e-?mail|phone|contact|recruiter)[^\]\n<>]{0,40}\]\s*(?:<\/p>)?\s*$/i;
/** Drop `<br>`s and elements left with no content once the signature text is cut. */
function dropEmptyElements(html) {
    let out = html.replace(/<br\s*\/?>/gi, '');
    for (;;) {
        const next = out.replace(/<([a-z][a-z0-9]*)\b[^>]*>\s*<\/\1>/gi, '');
        if (next === out)
            return out;
        out = next;
    }
}
/**
 * Remove a signature the model wrote on its own after the fixed follow-up closing.
 *
 * `generate_followup_email` ends with «Kind Regards,» and the recruiter signature is
 * appended from `users.email_signature` at preview/send time (see
 * {@link appendSignature}). A model that "helpfully" signs off (`[Your Name]`,
 * «EER Global», a made-up recruiter) would give the client a double or placeholder
 * signature. The prompt forbids it; this is the safety net for the model ignoring
 * the rule and for artifacts generated before the rule existed.
 *
 * Rules:
 *   - text after the LAST «Kind Regards» (≤ 300 chars of text) is cut; the closing
 *     tags that followed it are kept so the HTML stays balanced, empty elements are
 *     dropped;
 *   - with no closing present, a trailing bracketed placeholder line
 *     (`[Your Name]`, `[Recruiter Signature]`…) is removed;
 *   - anything else is returned unchanged (idempotent).
 */
export function stripFollowupSignaturePlaceholder(body) {
    if (!body)
        return body;
    let last = null;
    for (const m of body.matchAll(FOLLOWUP_CLOSING_RE))
        last = m;
    if (last) {
        const cut = last.index + last[0].length;
        const tail = body.slice(cut);
        const tailText = tail
            .replace(/<[^>]*>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .trim();
        if (tailText.length > 0 && tailText.length <= MAX_SIGNATURE_TAIL_CHARS) {
            const tags = tail.match(/<\/?[a-z][a-z0-9]*\b[^>]*>/gi) ?? [];
            return body.slice(0, cut) + dropEmptyElements(tags.join(''));
        }
        return body;
    }
    return body.replace(SIGNATURE_PLACEHOLDER_RE, '').replace(/\s+$/, '');
}
/**
 * Default CC of the client follow-up email: the union of `projects.data.cc_emails`
 * (header CC scraped off the intake email), `projects.data.contact_cc[].email`
 * (normalized CC from the intro) and `extra` (the project's CC contacts, KS-02),
 * deduplicated case-insensitively in first-seen order. Addresses in `exclude`
 * (the To) are skipped — a client listed in both fields gets the email twice.
 * Blank and non-string entries are ignored; the original casing is kept.
 */
export function followupDefaultCc(data, extra = [], exclude = []) {
    const seen = new Set(exclude.map((e) => (typeof e === 'string' ? e.trim().toLowerCase() : '')).filter(Boolean));
    const out = [];
    const add = (v) => {
        if (typeof v !== 'string')
            return;
        const email = v.trim();
        const key = email.toLowerCase();
        if (!email || seen.has(key))
            return;
        seen.add(key);
        out.push(email);
    };
    const ccEmails = data?.['cc_emails'];
    if (Array.isArray(ccEmails))
        ccEmails.forEach(add);
    const contactCc = data?.['contact_cc'];
    if (Array.isArray(contactCc)) {
        for (const c of contactCc) {
            if (c && typeof c === 'object')
                add(c.email);
        }
    }
    extra.forEach(add);
    return out;
}
// ---------------------------------------------------------------------------
// B1b / KS-17 — outbound MIME attachments
// ---------------------------------------------------------------------------
/**
 * One outbound attachment, carried as base64 END TO END (never a `Buffer`).
 *
 * Two reasons, both load-bearing:
 *   - this module is `@eer/shared`, imported by `@eer/web`, where `Buffer` does
 *     not exist — a Buffer field here would break the browser build;
 *   - base64 is exactly what MS Graph wants (`fileAttachment.contentBytes`) and
 *     what nodemailer accepts (`{ content, encoding: 'base64' }`), so nothing has
 *     to re-encode on the way out.
 */
export const outboundAttachmentSchema = z.object({
    filename: z.string().min(1),
    mimeType: z.string().min(1),
    /** base64 payload (no data: prefix). */
    contentBase64: z.string(),
    /**
     * Content-ID for an INLINE attachment referenced from the body as
     * `<img src="cid:…">` (KS-06 ч.2 — the signature logo). Absent for a normal
     * file attachment, which is listed by the client instead of being drawn in place.
     *
     * The two transports spell this differently — Graph wants `contentId` + `isInline`
     * on the fileAttachment, nodemailer wants `cid` — so it is carried here in a
     * transport-neutral form and translated at each branch.
     */
    contentId: z.string().min(1).optional(),
});
/**
 * Total attachment budget for ONE message, measured on the BASE64 payload —
 * i.e. on what actually travels, not on the decoded file size.
 *
 * 4 MB is the MS Graph `POST /me/sendMail` request-body ceiling: a bigger
 * request is rejected outright and, because the send runs inside the caller's
 * `$transaction`, the whole send would roll back. Anything over budget is
 * delivered as a link instead, so the message always goes out.
 * (Graph's documented escape hatch for larger files is a draft + upload session —
 * deliberately out of scope; see the B1b ADR.)
 */
export const ATTACHMENT_TOTAL_LIMIT_BYTES = 4 * 1024 * 1024;
/** Base64 payload size of a set of attachments, in bytes. */
export function attachmentsTotalBytes(attachments) {
    return attachments.reduce((sum, a) => sum + a.contentBase64.length, 0);
}
/** True when the set fits in one Graph request (`ATTACHMENT_TOTAL_LIMIT_BYTES`). */
export function attachmentsFitInOneMessage(attachments) {
    return attachmentsTotalBytes(attachments) <= ATTACHMENT_TOTAL_LIMIT_BYTES;
}
/**
 * Stage-5 items that carry their OWN document as a file (B1b/KS-17). Mirrors the
 * shape of {@link SIGNATURE_ITEM_KEYS}: the doc type attached is simply the item's
 * mapped artifact (`STAGE5_ARTIFACT_TYPE`), so there is no second key→type table.
 *
 * `final_package` is ABSENT here because it carries not one but ALL its package
 * documents — see {@link shouldAttachPackage} (E05-F06-T11 replaced the PK-QA-36
 * signed R2 links in the body). `client_followup_email` and
 * `internal_notification` are prose messages with no document to attach.
 */
export const ATTACHMENT_ITEM_KEYS = [
    'sourcing_sheet_email',
    'kickoff_sheet',
];
/** True when `itemKey` should ship its mapped artifact as a file attachment. */
export function shouldAttachDocument(itemKey) {
    return ATTACHMENT_ITEM_KEYS.includes(itemKey);
}
/**
 * E05-F06-T11 — items that ship the whole document PACKAGE as file attachments
 * (every generated package document, rendered server-side to `.docx`).
 *
 * Before this the Launched-stage composer pasted one presigned R2 url per
 * document into the body: long, unreadable, and dead after the presign expiry.
 * Now the files travel with the message and the body only names them
 * ({@link finalPackageBody}); a document that does not fit the message budget is
 * linked under its short label, never as a bare url.
 */
export const PACKAGE_ATTACHMENT_ITEM_KEYS = ['final_package'];
/** True when `itemKey` should ship every package document as an attachment. */
export function shouldAttachPackage(itemKey) {
    return PACKAGE_ATTACHMENT_ITEM_KEYS.includes(itemKey);
}
/** Minimal HTML text escape for labels placed into a composed email body. */
function escapeHtmlText(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/**
 * The pre-composed Final Package body (E05-F06-T11): a short preamble plus the
 * NAMES of the attached documents — no urls. With no documents the preamble alone
 * is returned (a project may reach Launched with nothing generated).
 */
export function finalPackageBody(labels) {
    const clean = labels.map((l) => l.trim()).filter(Boolean);
    if (clean.length === 0) {
        return '<p>Please find your final package documents attached to this email.</p>';
    }
    const items = clean.map((l) => `<li>${escapeHtmlText(l)}</li>`).join('');
    return `<p>Please find your final package documents attached to this email:</p><ul>${items}</ul>`;
}
/**
 * Append "document → link" lines for package documents that could not travel as
 * attachments (over the message budget). The url sits ONLY in `href`; the reader
 * sees the short document label (E05-F06-T11: no long storage urls in the text).
 * Plain-text / markdown bodies get a markdown link (rendered to `<a>` by the
 * email wrapper), HTML bodies get a real anchor.
 */
export function appendDocumentLinks(body, links) {
    if (links.length === 0)
        return body;
    if (looksLikeHtml(body)) {
        const items = links
            .map((l) => `<li><a href="${escapeHtmlText(l.url)}">${escapeHtmlText(l.label)}</a></li>`)
            .join('');
        return `${body}<p>Download:</p><ul>${items}</ul>`;
    }
    const lines = links.map((l) => `[${l.label}](${l.url})`).join('\n');
    return `${body}\n\n${lines}`;
}
/**
 * The one-line stand-in that replaces a document INLINED into an email body once
 * that document travels as a real file (E05-F06-T10).
 *
 * Before B1b/KS-17 the only way to deliver a sheet was to paste it into the body,
 * so the "Send to Sourcing Team" template ends with `{{kickoff_sheet}}` and the
 * bare-document composition used the sheet as the body. Now that the same sheet
 * ships as a `.docx`, keeping that paste means the recipient gets the identical
 * text twice — once badly reflowed in the message, once in the attachment.
 *
 * The covering prose stays (it is the part a person wrote); only the document dump
 * becomes this pointer. Keyed off {@link shouldAttachDocument}, so the body and the
 * attachment can never disagree about who carries the document.
 *
 * `label` comes from the shared `exportLabel` dictionary (a fixed set of literals),
 * never from user input — this returns HTML and does not escape.
 */
export function attachedDocumentNote(label) {
    return `<p><i>${label} is attached to this email as a Word document.</i></p>`;
}
// ---------------------------------------------------------------------------
// Recipient de-dup helpers (T03 acceptance: "получатели без дублей")
// ---------------------------------------------------------------------------
/** Split a comma/semicolon-separated To/CC string into trimmed, non-empty parts. */
export function parseRecipients(value) {
    if (!value)
        return [];
    return value
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter(Boolean);
}
/**
 * Add `email` to a To-field string without duplicates (dashboard.js
 * `_addRecipient`): case-insensitive existence check, comma-separated join.
 */
export function addRecipient(current, email) {
    const add = email.trim();
    if (!add)
        return current;
    const existing = parseRecipients(current);
    const lower = existing.map((e) => e.toLowerCase());
    if (lower.includes(add.toLowerCase()))
        return existing.join(', ');
    return [...existing, add].join(', ');
}
/** Replace the To-field with a team's member emails (dashboard.js `_onSrcTeamSelect`). */
export function recipientsFromTeam(members) {
    return members
        .map((m) => (m?.email ?? '').trim())
        .filter(Boolean)
        .join(', ');
}
/** Loose RFC-ish email check (dashboard.js send-validation regex parity). */
export function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}
//# sourceMappingURL=email-send.js.map